import React, { cloneElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import merge from 'lodash/merge';
import DataSet from '../../../pro/es/data-set/DataSet';
import Button from '../../../pro/es/button';
import { $l } from '../../../pro/es/locale-context';
import { ButtonColor, FuncType } from '../../../pro/es/button/enum';
import TabGroups from './tab-groups';
import TabsContext from '../TabsContext';
import { getDefaultActiveKey, normalizePanes } from '../utils';
function processPaneToData(pane, key, group) {
    const { tab, title, sort, showCount, disabled } = pane;
    return {
        key,
        tab,
        title,
        sort,
        showCount,
        group,
        disabled,
    };
}
function normalizePanesToData(pair) {
    const [totalPanelsMap, groupPanelMap] = pair;
    const data = [];
    if (groupPanelMap.size > 0) {
        groupPanelMap.forEach((paneMap, key) => (paneMap.panelsMap.forEach((pane, paneKey) => data.push(processPaneToData(pane, paneKey, key)))));
    }
    else {
        totalPanelsMap.forEach((pane, paneKey) => data.push(processPaneToData(pane, paneKey)));
    }
    return data;
}
const CustomizationSettings = function CustomizationSettings(props) {
    const { modal } = props;
    const { customized, saveCustomized, totalPanelsMap, groupedPanelsMap, defaultActiveKey, actuallyDefaultActiveKey, propActiveKey, children, restoreDefault } = useContext(TabsContext);
    const oldCustomized = useMemo(() => customized ? merge({}, customized) : { panes: {} }, [customized]);
    const tempCustomized = useRef(oldCustomized);
    const [customizedPanes, setCustomizedPanes] = useState(() => [totalPanelsMap, groupedPanelsMap]);
    const [defaultKey, setDefaultKey] = useState(actuallyDefaultActiveKey);
    const panesDataSet = useMemo(() => new DataSet({
        data: normalizePanesToData(customizedPanes),
        paging: false,
        primaryKey: 'key',
        fields: [
            { name: 'group', group: true },
        ],
        events: {
            update({ record, name, value }) {
                const { current } = tempCustomized;
                const { key } = record;
                if (!current.panes[key]) {
                    current.panes[key] = {};
                }
                current.panes[key][name] = value;
            },
        },
    }), [customizedPanes, tempCustomized]);
    const handleRestorePanes = useCallback((e) => {
        e.stopPropagation();
        const [newTotalPanelsMap, newGroupedPanelsMap] = normalizePanes(children);
        setCustomizedPanes([newTotalPanelsMap, newGroupedPanelsMap]);
        setDefaultKey(getDefaultActiveKey(newTotalPanelsMap, newGroupedPanelsMap, { activeKey: propActiveKey, defaultActiveKey }));
        tempCustomized.current = { panes: {} };
    }, [defaultActiveKey, propActiveKey, tempCustomized, children]);
    const handleDefaultKeyChange = useCallback((key) => {
        tempCustomized.current.defaultActiveKey = key;
        setDefaultKey(key);
    }, [tempCustomized]);
    const handleOk = useCallback(() => {
        saveCustomized(tempCustomized.current);
        if (modal) {
            modal.close();
        }
    }, [saveCustomized, tempCustomized, modal]);
    useEffect(() => {
        if (modal) {
            modal.update({
                footer: (okBtn, cancelBtn) => (React.createElement(React.Fragment, null,
                    cloneElement(okBtn, { onClick: handleOk }),
                    cancelBtn,
                    React.createElement(Button, { hidden: !restoreDefault, funcType: FuncType.link, color: ButtonColor.primary, onClick: handleRestorePanes, style: { float: 'right' } }, $l('Tabs', 'restore_default')))),
            });
        }
    }, [modal]);
    return (React.createElement(TabGroups, { dataSet: panesDataSet, defaultKey: defaultKey, onDefaultKeyChange: handleDefaultKeyChange }));
};
CustomizationSettings.displayName = 'CustomizationSettings';
export default observer(CustomizationSettings);
